const STORAGE_PREFIX = 'interviewQuestionVariants:'

type QuestionVariantHistory = {
  variants: string[]
  currentIndex: number
}

type VacancyHistory = Record<string, QuestionVariantHistory>

const isStorageAvailable = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'

const loadVacancyHistory = (vacancyId: number): VacancyHistory => {
  if (!isStorageAvailable()) return {}
  const key = `${STORAGE_PREFIX}${vacancyId}`
  const raw = window.sessionStorage.getItem(key)
  if (!raw) return {}
  try {
    return JSON.parse(raw) as VacancyHistory
  } catch {
    return {}
  }
}

const saveVacancyHistory = (vacancyId: number, history: VacancyHistory) => {
  if (!isStorageAvailable()) return
  const key = `${STORAGE_PREFIX}${vacancyId}`
  window.sessionStorage.setItem(key, JSON.stringify(history))
}

const normalizeTextList = (list: string[]) => list.filter((item) => item && item.trim().length > 0)

const ensureEntry = (
  history: VacancyHistory,
  questionKey: string,
  initialText: string
): QuestionVariantHistory => {
  const existing = history[questionKey]
  if (existing && existing.variants.length > 0) {
    return existing
  }
  const variants = normalizeTextList([initialText])
  const entry: QuestionVariantHistory = {
    variants,
    currentIndex: variants.length > 0 ? variants.length - 1 : 0,
  }
  history[questionKey] = entry
  return entry
}

const addRegeneratedVariant = (
  vacancyId: number,
  questionKey: string,
  previousText: string,
  newText: string
) => {
  if (!previousText && !newText) return

  const history = loadVacancyHistory(vacancyId)
  const entry = ensureEntry(history, questionKey, previousText)

  const variants = [...entry.variants]

  if (!variants.includes(previousText) && previousText.trim().length > 0) {
    variants.push(previousText)
  }

  if (newText.trim().length > 0 && variants[variants.length - 1] !== newText) {
    variants.push(newText)
  }

  entry.variants = normalizeTextList(variants)
  entry.currentIndex = entry.variants.length > 0 ? entry.variants.length - 1 : 0

  history[questionKey] = entry
  saveVacancyHistory(vacancyId, history)
}

const getPreviousVariant = (
  vacancyId: number,
  questionKey: string
): string | null => {
  const history = loadVacancyHistory(vacancyId)
  const entry = history[questionKey]
  if (!entry || entry.variants.length === 0) {
    return null
  }
  if (entry.currentIndex <= 0) {
    return null
  }
  entry.currentIndex -= 1
  history[questionKey] = entry
  saveVacancyHistory(vacancyId, history)
  return entry.variants[entry.currentIndex] ?? null
}

const getNextVariant = (
  vacancyId: number,
  questionKey: string
): string | null => {
  const history = loadVacancyHistory(vacancyId)
  const entry = history[questionKey]
  if (!entry || entry.variants.length === 0) {
    return null
  }
  if (entry.currentIndex >= entry.variants.length - 1) {
    return null
  }
  entry.currentIndex += 1
  history[questionKey] = entry
  saveVacancyHistory(vacancyId, history)
  return entry.variants[entry.currentIndex] ?? null
}

const clearVacancyHistory = (vacancyId: number) => {
  if (!isStorageAvailable()) return
  const key = `${STORAGE_PREFIX}${vacancyId}`
  window.sessionStorage.removeItem(key)
}

export const questionVariantsSessionService = {
  addRegeneratedVariant,
  getPreviousVariant,
  getNextVariant,
  clearVacancyHistory,
}


